import * as THREE from 'three';
import { WebGLElement, findThreeParent } from './base.js';

// <glow-network count="50" radius="16" color="#7aa2f7" shape="sphere" position="0 0 -3"> —
// scattered glowing nodes with glowing lines between nearby ones (constellation / network
// look). Unlit + additive blending; each node has its OWN material so per-node brightness
// varies and animates (random phase/speed pulse) — this drives a real UnrealBloom on a
// <three-scene bloom>. `shape="box"|"sphere"`. Slow drift for life. Parents into <three-scene>.
export class GlowNetwork extends WebGLElement {
  static properties = {
    ...WebGLElement.properties,
    count: { type: Number },
    radius: { type: Number },
    color: { type: String },
    shape: { type: String },
    spin: { type: Number },
  };

  constructor() {
    super();
    this.count = 50;
    this.radius = 16;
    this.color = '#7aa2f7';
    this.shape = 'box';
    this.spin = 0.02; // rad/sec gentle drift
    this._group = null;
    this._cb = null;
    this._nodes = [];
    this._col = null;
    this._t = 0;
  }

  createObject3D() {
    const group = new THREE.Group();
    const n = Math.max(2, this.count || 50);
    const R = this.radius || 16;
    const col = new THREE.Color(this.color || '#7aa2f7');
    this._col = col;
    this._nodes = [];

    // scattered glowing nodes — shared geometry, per-node material (varying brightness)
    const sphere = (this.shape || 'box') === 'sphere';
    const geo = sphere
      ? new THREE.SphereGeometry(0.12, 16, 12)
      : new THREE.BoxGeometry(0.16, 0.16, 0.16);
    const pts = [];
    for (let i = 0; i < n; i++) {
      const p = new THREE.Vector3(
        (Math.random() * 2 - 1) * R,
        (Math.random() * 2 - 1) * R * 0.6,
        (Math.random() * 2 - 1) * R * 0.35,
      );
      pts.push(p);
      // skewed random: most nodes dim, a few bright (bright ones bloom hardest)
      const intensity = 0.35 + Math.random() * Math.random() * 1.0;
      const mat = new THREE.MeshBasicMaterial({
        color: col.clone().multiplyScalar(intensity),
        transparent: true,
        opacity: 1.0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      });
      const node = new THREE.Mesh(geo, mat);
      node.position.copy(p);
      if (!sphere) node.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
      node.scale.setScalar(0.5 + Math.random());
      group.add(node);
      this._nodes.push({
        mat,
        base: intensity,
        phase: Math.random() * Math.PI * 2,
        speed: 0.2 + Math.random() * 0.6, // rad/sec pulse (slow, subtle)
      });
    }

    // glowing lines between nearby boxes
    const maxD = R * 0.42;
    const linePos = [];
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        if (pts[i].distanceTo(pts[j]) < maxD) {
          linePos.push(pts[i].x, pts[i].y, pts[i].z, pts[j].x, pts[j].y, pts[j].z);
        }
      }
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePos, 3));
    const lineMat = new THREE.LineBasicMaterial({
      color: col,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    group.add(new THREE.LineSegments(lineGeo, lineMat));

    this._group = group;
    return group;
  }

  connectedCallback() {
    super.connectedCallback();
    const scene = findThreeParent(this);
    if (!scene?.addFrameCallback) return;
    this._scene = scene;
    const spin = Number(this.spin) || 0;
    const scratch = new THREE.Color();
    this._cb = (dt) => {
      const d = dt || 0.016;
      this._t += d;
      if (spin && this._group) this._group.rotation.y += spin * d;
      // per-node pulse: brightness wobbles around its base intensity (drives bloom)
      for (const nd of this._nodes) {
        const pulse = 0.88 + 0.12 * Math.sin(this._t * nd.speed + nd.phase);
        nd.mat.color.copy(scratch.copy(this._col).multiplyScalar(nd.base * pulse));
      }
    };
    scene.addFrameCallback(this._cb);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._cb) this._scene?.removeFrameCallback?.(this._cb);
  }
}

customElements.define('glow-network', GlowNetwork);
